import { MeshBasicMaterial } from 'three';

import { useAppSelector } from '../../components/hooks';
import { getHighVisibilityMode } from '../../store/selectors/getHighVisibilityMode';

export type DimensionStyleVariant = 'standard' | 'snap' | 'xyComponent' | 'userCreated';

export const DIMENSION_OPACITY = 0.8;

const HIGH_VISIBILITY_DIMENSION_COLOR = 0x00ffff;
const DIMENSION_LABEL_OUTLINE_OPACITY = 0.55;

interface DimensionVariantStyle {
  readonly lineColor: number;
  readonly labelColor: number;
  readonly outlineColor: number;
}

const DIMENSION_STYLES: Record<DimensionStyleVariant, DimensionVariantStyle> = {
  standard: {
    lineColor: 0xffc4c0,
    labelColor: 0xffc4c0,
    outlineColor: 0x000000,
  },
  snap: {
    lineColor: 0xd25353,
    labelColor: 0xd25353,
    outlineColor: 0xffffff,
  },
  xyComponent: {
    lineColor: 0xd2b253,
    labelColor: 0xd2b253,
    outlineColor: 0x000000,
  },
  userCreated: {
    lineColor: 0x000ddd,
    labelColor: 0xffffff,
    outlineColor: 0x000000,
  },
};

const HIGH_VISIBILITY_STANDARD_STYLE: DimensionVariantStyle = {
  ...DIMENSION_STYLES.standard,
  lineColor: HIGH_VISIBILITY_DIMENSION_COLOR,
  labelColor: HIGH_VISIBILITY_DIMENSION_COLOR,
};

function getDimensionStyle(styleVariant: DimensionStyleVariant, highVisibilityMode: boolean) {
  if (highVisibilityMode && styleVariant === 'standard') return HIGH_VISIBILITY_STANDARD_STYLE;

  return DIMENSION_STYLES[styleVariant];
}

export function getDimensionLineColor(styleVariant: DimensionStyleVariant, highVisibilityMode: boolean) {
  return getDimensionStyle(styleVariant, highVisibilityMode).lineColor;
}

const SHARED_MATERIAL_OPTIONS = { transparent: true, opacity: DIMENSION_OPACITY, depthTest: false };
const materialsByColor = new Map<number, MeshBasicMaterial>();

/** Dimension materials differ only by color, so one shared instance per color serves every dimension in the scene. */
function getMaterialForColor(color: number) {
  const existingMaterial = materialsByColor.get(color);
  if (existingMaterial) return existingMaterial;

  const material = new MeshBasicMaterial({ ...SHARED_MATERIAL_OPTIONS, color });
  materialsByColor.set(color, material);
  return material;
}

export interface DimensionLabelOptions {
  readonly color: number;
  readonly opacity: number;
  readonly outlineColor: number;
  readonly outlineOpacity: number;
}

export function getDimensionLabelOptions(
  styleVariant: DimensionStyleVariant,
  highVisibilityMode: boolean,
): DimensionLabelOptions {
  const style = getDimensionStyle(styleVariant, highVisibilityMode);

  return {
    color: style.labelColor,
    opacity: DIMENSION_OPACITY,
    outlineColor: style.outlineColor,
    outlineOpacity: DIMENSION_LABEL_OUTLINE_OPACITY,
  };
}

export function useDimensionStyle(styleVariant: DimensionStyleVariant) {
  const highVisibilityMode = useAppSelector(getHighVisibilityMode);

  const style = getDimensionStyle(styleVariant, highVisibilityMode);
  const labelOptions = getDimensionLabelOptions(styleVariant, highVisibilityMode);

  return {
    material: getMaterialForColor(style.lineColor),
    labelMaterial: getMaterialForColor(style.labelColor),
    labelOptions,
  };
}

export function useDimensionLabelOptions(styleVariant: DimensionStyleVariant) {
  return useDimensionStyle(styleVariant).labelOptions;
}
